import { Label } from "layout/typography/Label";
import { Button } from "primereact/button";
import { Chips, ChipsChangeParams } from "primereact/chips";
import { Dialog } from "primereact/dialog";
import { FileUpload } from "primereact/fileupload";
import { InputText } from "primereact/inputtext";
import { Message } from "primereact/message";
import { MultiSelect } from "primereact/multiselect";
import { useEffect, useRef, useState } from "react";
import { usePreviewPriorAuthFileMutation } from "store/queries/priorAuthorizations";
import styled from "styled-components";
import {
  IPriorAuthListDetail,
  IPriorAuthPreview,
} from "types/PriorAuth/PriorAuth";

const StyledMessage = styled(Message)`
  justify-content: flex-start;
`;

const CodeCount = styled.small`
  color: var(--gray-700);
`;

export interface IPriorAuthListDraft {
  id?: string;
  name: string;
  payerIds: number[];
  planKeywords: string[];
  cptCodes: string[];
  /** Set when the operator picked a file this session; replaces the code list on save. */
  file: File | null;
}

export const emptyDraft = (): IPriorAuthListDraft => ({
  name: "",
  payerIds: [],
  planKeywords: [],
  cptCodes: [],
  file: null,
});

export const draftFromList = (
  list: IPriorAuthListDetail
): IPriorAuthListDraft => ({
  id: list.id,
  name: list.name,
  payerIds: list.payerIds,
  planKeywords: list.planKeywords,
  cptCodes: list.cptCodes,
  file: null,
});

interface IPriorAuthListDialogProps {
  visible: boolean;
  value: IPriorAuthListDraft;
  payerOptions: Array<{ label: string; value: number }>;
  loadingPayers: boolean;
  isNew: boolean;
  saving: boolean;
  onSave: (draft: IPriorAuthListDraft) => void;
  onHide: () => void;
}

/**
 * CLE-2758 Part 1 — one prior-auth list, created or edited in isolation.
 *
 * Modelled on the benefit-rule dialog (Ani pointed at it directly): payers as a multi-select, plans
 * as chips. The difference is the CPT list, which arrives as a file on create and is editable as
 * chips afterwards — Ani: "edit changes payers plans and code list".
 *
 * The file is PREVIEWED before it is accepted, so the operator sees what parsed and what was thrown
 * away before anything is saved. A file that parses replaces the draft's codes immediately, which is
 * why the chips below it update the moment a file is chosen: what you see is exactly what saves.
 */
export function PriorAuthListDialog({
  visible,
  value,
  payerOptions,
  loadingPayers,
  isNew,
  saving,
  onSave,
  onHide,
}: IPriorAuthListDialogProps) {
  const [draft, setDraft] = useState<IPriorAuthListDraft>(value);
  const [preview, setPreview] = useState<IPriorAuthPreview | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const fileRef = useRef<FileUpload>(null);

  const [previewFile, { isLoading: isPreviewing }] =
    usePreviewPriorAuthFileMutation();

  useEffect(() => {
    if (visible) {
      setDraft(value);
      setPreview(null);
      setFileError(null);
      fileRef.current?.clear();
    }
  }, [visible, value]);

  const set = (patch: Partial<IPriorAuthListDraft>) =>
    setDraft((current) => ({ ...current, ...patch }));

  const handleFile = async (file: File) => {
    setFileError(null);
    try {
      const response = await previewFile(file).unwrap();
      setPreview(response.data);
      set({ cptCodes: response.data.cptCodes, file });
    } catch (e) {
      const err = e as { data?: { message?: string }; message?: string };
      setFileError(
        err?.data?.message ?? err?.message ?? "Could not read that file."
      );
      setPreview(null);
      set({ file: null });
      fileRef.current?.clear();
    }
  };

  const canSave =
    draft.name.trim().length > 0 && draft.cptCodes.length > 0 && !saving;

  let chooseLabel = "Choose file";
  if (isPreviewing) chooseLabel = "Reading…";
  else if (draft.file) chooseLabel = draft.file.name;

  return (
    <Dialog
      header={isNew ? "New prior auth list" : "Edit prior auth list"}
      visible={visible}
      onHide={onHide}
      style={{ width: "min(52rem, 95vw)" }}
      data-testid="priorAuthListDialog"
      footer={
        <div className="flex justify-content-end gap-2">
          <Button
            type="button"
            label="Cancel"
            className="p-button-text"
            data-testid="priorAuthListDialog.cancel"
            onClick={onHide}
          />
          <Button
            type="button"
            label={isNew ? "Create list" : "Save list"}
            loading={saving}
            disabled={!canSave}
            data-testid="priorAuthListDialog.save"
            onClick={() => onSave(draft)}
          />
        </div>
      }
    >
      <div className="formgrid grid">
        <div className="field col-12">
          <Label htmlFor="priorAuthListDialog.name">List name</Label>
          <InputText
            id="priorAuthListDialog.name"
            data-testid="priorAuthListDialog.name"
            className="w-full"
            value={draft.name}
            placeholder="e.g. UHC outpatient prior auth"
            onChange={(e) => set({ name: e.target.value })}
          />
        </div>

        <div className="field col-12 md:col-6">
          <Label htmlFor="priorAuthListDialog.payerIds">Payers</Label>
          <MultiSelect
            id="priorAuthListDialog.payerIds"
            data-testid="priorAuthListDialog.payerIds"
            className="w-full"
            display="chip"
            filter
            options={payerOptions}
            disabled={loadingPayers}
            value={draft.payerIds}
            placeholder="All payers"
            onChange={(e) => set({ payerIds: e.value ?? [] })}
          />
        </div>

        <div className="field col-12 md:col-6">
          <Label htmlFor="priorAuthListDialog.planKeywords">
            Plans / products (optional)
          </Label>
          <Chips
            id="priorAuthListDialog.planKeywords"
            data-testid="priorAuthListDialog.planKeywords"
            className="w-full"
            value={draft.planKeywords}
            placeholder="Leave empty for every plan"
            onChange={(e: ChipsChangeParams) =>
              set({ planKeywords: e.value ?? [] })
            }
          />
        </div>

        <div className="field col-12">
          <Label htmlFor="priorAuthListDialog.file">
            CPT code file (CSV or XLSX)
          </Label>
          <FileUpload
            ref={fileRef}
            mode="basic"
            name="file"
            accept=".csv,.xlsx"
            auto
            chooseLabel={chooseLabel}
            customUpload
            uploadHandler={(e) => handleFile(e.files[0])}
          />
          <small className="text-gray-500">
            One CPT code per line, under a <code>CPT Code</code> column. Ranges
            are not supported.
          </small>
        </div>

        {fileError && (
          <div className="field col-12">
            <StyledMessage
              severity="error"
              text={fileError}
              data-testid="priorAuthListDialog.fileError"
            />
          </div>
        )}

        {preview && (
          <div className="field col-12">
            <StyledMessage
              severity={
                preview.rangeLikeExamples.length > 0 ? "warn" : "success"
              }
              data-testid="priorAuthListDialog.preview"
              text={[
                `${preview.cptCodeCount} CPT codes read`,
                preview.duplicateRows > 0
                  ? `${preview.duplicateRows} duplicates ignored`
                  : null,
                preview.droppedInvalidCpt > 0
                  ? `${preview.droppedInvalidCpt} rows skipped`
                  : null,
                // Surfaced by name because a file full of ranges means the operator has the wrong
                // file, and "N rows skipped" would not tell them that.
                preview.rangeLikeExamples.length > 0
                  ? `ranges are not supported (e.g. ${preview.rangeLikeExamples[0]})`
                  : null,
              ]
                .filter(Boolean)
                .join(" · ")}
            />
          </div>
        )}

        <div className="field col-12">
          <Label htmlFor="priorAuthListDialog.cptCodes">CPT codes</Label>
          <Chips
            id="priorAuthListDialog.cptCodes"
            data-testid="priorAuthListDialog.cptCodes"
            className="w-full"
            value={draft.cptCodes}
            placeholder="Add or remove individual codes"
            onChange={(e: ChipsChangeParams) =>
              set({
                cptCodes: (e.value ?? []).map((code: string) =>
                  String(code).trim().toUpperCase()
                ),
              })
            }
          />
          <CodeCount>
            {draft.cptCodes.length} code
            {draft.cptCodes.length === 1 ? "" : "s"} on this list
          </CodeCount>
        </div>
      </div>
    </Dialog>
  );
}
